import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../utils/posthogUtils';
import { CommunityHeader } from './CommunityHeader';
import { CommunityUpdates } from './CommunityUpdates';
// import { RecommendedCommunities } from './RecommendedCommunities';
import { CommunityMembers } from './CommunityMembers';
// import { RecommendedProjects } from './RecommendedProjects';
// import { RecommendedResources } from './RecommendedResources';
import { NotFound } from '../common/layout/NotFound';
import { Community } from './types';
import { CommunityAboutSection } from './CommunityAboutSection';
import { useAuth } from '../../lib/AuthContext';
import { CommunityMember } from './types';

export const CommunityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [community, setCommunity] = useState<Community | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommunity();
  }, [id]);

  const fetchCommunity = async () => {
    if (!id) return;

    try {
      // Fetch community details, workspace, and members (with user profile fields) in a single query
      const { data: communityData, error: communityError } = await supabase
        .from('communities')
        .select(`
          *,
          workspace:workspaces!inner (
            workspace_id,
            workspace_name,
            logo_url
          ),
          members:community_members!community_id (
            user_id,
            role,
            users (
              id,
              first_name,
              last_name,
              profile_image_url
            )
          )
        `)
        .eq('id', id)
        .eq('members.role', 'member')
        .single();

      if (communityError) throw communityError;

      const members = (communityData.members || []).map((m: { user_id: string; role: string; users: { id: string; first_name: string | null; last_name: string | null; profile_image_url: string | null; } }) => ({
        user_id: m.user_id,
        role: m.role,
        first_name: m.users?.first_name ?? null,
        last_name: m.users?.last_name ?? null,
        profile_image_url: m.users?.profile_image_url ?? null,
      }));

      const community = {
        ...communityData,
        members,
        total_members: members.length
      };

      setCommunity(community);
      // Track community view after successful load
      captureCustomEvent('community_view', {
        eventProperties: {
          community_id: community.id,
          community_name: community.name,
          is_member: members.some((m: CommunityMember) => m.user_id === user?.id)
        }
      });

    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'community_page',
          community_id: id,
          action: 'fetch_community'
        }
      });
      setCommunity(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!community) {
    return (
      <NotFound
        title="Community Not Found"
        message="The community you're looking for doesn't exist or has been removed."
      />
    );
  }

  return (
    <div className="space-y-6">
      <CommunityHeader community={community} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          <CommunityUpdates communityId={community.id} />
          {/* <RecommendedCommunities currentCommunityId={community.id} /> */}
          <CommunityMembers community={community} />
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          <CommunityAboutSection community={community} />
          {/* Recommended Project and resources will be re-enabled once we refresh them and tie them to workspaces and communities */}
          {/* <RecommendedProjects communityId={community.id} />
          <RecommendedResources communityId={community.id} /> */}
        </div>
      </div>
    </div>
  );
};